import {
  Box,
  Button,
  CircularProgress,
  Grid,
  Link,
  TextField,
  Typography,
} from '@material-ui/core';
import { Form, Formik, FormikProps } from 'formik';
import { ApiFunctions } from 'hydra-data-types';
import { Dispatch, FunctionComponent, SetStateAction, useState } from 'react';
import * as yup from 'yup';
import { makeFormErrors, resetPassword } from '../../api/AuthApi';
import buttonArrow from '../../assets/svgs/arrow-right.svg';
import { AlertBar, AlertStatus } from '../../components/AlertBar';
import { isNonEmptyString } from '../../helpers/StringUtils';
import { Analytics } from '../../helpers/analytics/Analytics';
import { AnalyticsEventName } from '../../helpers/analytics/AnalyticsEvent';
import { ValidationMessages } from './types';

const { EMAIL_REQUIRED, INVALID_EMAIL } = ValidationMessages;

const SCHEMA: yup.ObjectSchema<ApiFunctions.DoesUserExistData> = yup
  .object()
  .shape({
    email: yup.string().email(INVALID_EMAIL).required(EMAIL_REQUIRED),
  })
  .required();

type Props = {
  setStep: Dispatch<SetStateAction<number>>;
  email: string;
};

export const ResetPasswordScreen: FunctionComponent<Props> = (props) => {
  const { email } = props;
  const [openAlert, setOpenAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState<string>(
    'If this account exists, you will receive an email with instructions to reset your password.'
  );
  const [alertStatus, setAlertStatus] = useState<AlertStatus>('success');
  const initialValues: ApiFunctions.DoesUserExistData = { email };

  async function onSubmit(values: ApiFunctions.DoesUserExistData) {
    try {
      await resetPassword(values.email);

      setOpenAlert(true);
      Analytics.track({ event: Analytics.Event.a_password_reset_sent });
    } catch (error) {
      handleErrors(error);
    }
  }

  function handleErrors(error: any) {
    const formErrors = makeFormErrors(error);

    if (formErrors !== undefined && 'email' in formErrors) {
      setOpenAlert(true);
    } else {
      setAlertMessage('Unable to reset password, please try again.');
      setAlertStatus('error');
      setOpenAlert(true);
    }

    Analytics.track({
      event: AnalyticsEventName.a_password_reset_failed,
      error,
    });
  }

  return (
    <Grid container direction="column" data-testid="reset-password-screen">
      <AlertBar copy={alertMessage} status={alertStatus} open={openAlert} onClose={setOpenAlert} />
      <Formik initialValues={initialValues} validationSchema={SCHEMA} onSubmit={onSubmit}>
        {(formikProps: FormikProps<ApiFunctions.DoesUserExistData>) => {
          const { values, touched, errors, handleBlur, handleChange, isSubmitting, isValid } =
            formikProps;

          return (
            <Form>
              <Grid container item direction="column">
                <Box width="100%" textAlign="center" marginBottom="40px">
                  <TextField
                    required
                    fullWidth
                    name="email"
                    id="email"
                    placeholder="example@email.com"
                    value={values.email}
                    type="email"
                    error={!!(isNonEmptyString(errors.email) && touched.email)}
                    helperText={isNonEmptyString(errors.email) && touched.email && errors.email}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    inputProps={{ style: { textAlign: 'center' } }}
                  />
                </Box>

                <Grid container item justifyContent="center">
                  <Box width="200px" display="flex" position="relative">
                    <Button
                      type="submit"
                      variant="contained"
                      color="primary"
                      size="large"
                      fullWidth
                      disabled={!isValid || isSubmitting}
                      endIcon={
                        <img src={buttonArrow} alt="next button" width="24px" height="24px" />
                      }
                    >
                      Reset
                    </Button>
                    {isSubmitting && <CircularProgress size={24} color="primary" />}
                  </Box>
                </Grid>
              </Grid>
            </Form>
          );
        }}
      </Formik>
      <Box textAlign="center" marginTop="50px">
        <Typography>
          Remembered your password?{' '}
          <Button
            display="inline"
            component={Link}
            color="primary"
            onClick={() => props.setStep(1)}
            underline="none"
          >
            Login
          </Button>
        </Typography>
      </Box>
    </Grid>
  );
};
